/**
 * Job-email dialog components, extracted from JobsEnhanced.jsx (Jun 2026)
 * to bring that 4,768-LOC file down to a maintainable size.
 *
 * EmailJobPhotosDialog  — bulk-email all job photos to a recipient (7-day links)
 * EmailJobReportDialog  — email a job completion report (optionally with photo links)
 *
 * Both dialogs share the same recipient input + sending state, so the parent
 * page passes those via props instead of duplicating state.
 */
import React from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Mail, FileText, Send, RefreshCw } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

// ─── Email Photos ────────────────────────────────────────────────────────
export function EmailJobPhotosDialog({
  open, onOpenChange, selectedJob, emailRecipient, setEmailRecipient,
  sendingEmail, setSendingEmail, token, toast,
}) {
  const handleSend = async () => {
    if (!emailRecipient || !emailRecipient.includes('@')) {
      toast({ title: 'Invalid Email', description: 'Please enter a valid email address', variant: 'destructive' });
      return;
    }
    setSendingEmail(true);
    try {
      const response = await axios.post(
        `${API}/jobs/${selectedJob.id}/send-photos`,
        { recipient_email: emailRecipient },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({ title: 'Photos Sent!', description: response.data.message });
      onOpenChange(false);
      setEmailRecipient('');
    } catch (error) {
      toast({
        title: 'Failed to Send',
        description: error.response?.data?.detail || 'Unable to send photos. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Mail className="w-5 h-5 text-[#1E88E5]" />
            <span>Email Job Photos</span>
          </DialogTitle>
          <DialogDescription>
            Send {selectedJob?.photos?.length || 0} photo(s) for "{selectedJob?.title}" to a recipient
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="email-recipient">Recipient Email</Label>
            <Input
              id="email-recipient"
              type="email"
              placeholder="customer@example.com"
              value={emailRecipient}
              onChange={(e) => setEmailRecipient(e.target.value)}
              className="w-full"
              data-testid="email-photos-recipient"
            />
            <p className="text-xs text-slate-500">
              Photos will be accessible for 7 days via secure links
            </p>
          </div>
        </div>

        <DialogFooter className="flex space-x-2">
          <Button
            variant="outline"
            onClick={() => { onOpenChange(false); setEmailRecipient(''); }}
            disabled={sendingEmail}
            data-testid="email-photos-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sendingEmail || !emailRecipient}
            className="bg-[#1E88E5] hover:bg-blue-700"
            data-testid="email-photos-send"
          >
            {sendingEmail ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Sending...</>) : (<><Send className="w-4 h-4 mr-2" />Send Photos</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Email Completion Report ────────────────────────────────────────────
export function EmailJobReportDialog({
  open, onOpenChange, selectedJob, emailRecipient, setEmailRecipient,
  includePhotosInReport, setIncludePhotosInReport, sendingEmail, setSendingEmail,
  token, toast,
}) {
  const handleSend = async () => {
    if (!emailRecipient || !emailRecipient.includes('@')) {
      toast({ title: 'Invalid Email', description: 'Please enter a valid email address', variant: 'destructive' });
      return;
    }
    setSendingEmail(true);
    try {
      const response = await axios.post(
        `${API}/jobs/${selectedJob.id}/send-report`,
        { recipient_email: emailRecipient, include_photos: includePhotosInReport },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({ title: 'Report Sent!', description: response.data.message });
      onOpenChange(false);
      setEmailRecipient('');
      setIncludePhotosInReport(false);
    } catch (error) {
      toast({
        title: 'Failed to Send',
        description: error.response?.data?.detail || 'Unable to send report. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-[#A78BFA]" />
            <span>Email Job Completion Report</span>
          </DialogTitle>
          <DialogDescription>
            Send a detailed completion report for "{selectedJob?.title}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="report-recipient">Recipient Email</Label>
            <Input
              id="report-recipient"
              type="email"
              placeholder="customer@example.com"
              value={emailRecipient}
              onChange={(e) => setEmailRecipient(e.target.value)}
              className="w-full"
              data-testid="email-report-recipient"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="include-photos"
              checked={includePhotosInReport}
              onCheckedChange={setIncludePhotosInReport}
              disabled={!selectedJob?.photos || selectedJob.photos.length === 0}
              data-testid="email-report-include-photos"
            />
            <Label htmlFor="include-photos" className="text-sm font-normal cursor-pointer">
              Include photos in report ({selectedJob?.photos?.length || 0} available)
            </Label>
          </div>

          <div className="bg-[#1A2433] rounded-lg p-3 text-xs text-slate-400">
            <p className="font-semibold mb-1">Report will include:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Job details and description</li>
              <li>Completion date and worker info</li>
              <li>Job duration</li>
              <li>Completion notes</li>
              {includePhotosInReport && <li>Photo links (valid for 7 days)</li>}
            </ul>
          </div>
        </div>

        <DialogFooter className="flex space-x-2">
          <Button
            variant="outline"
            onClick={() => { onOpenChange(false); setEmailRecipient(''); setIncludePhotosInReport(false); }}
            disabled={sendingEmail}
            data-testid="email-report-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sendingEmail || !emailRecipient}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="email-report-send"
          >
            {sendingEmail ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Sending...</>) : (<><Send className="w-4 h-4 mr-2" />Send Report</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
